/**
 * Initialize JavaScript libraries within a container.
 *
 * @param container The container element that holds the libraries to initialize.
 */
export function initContainer(container) {
    // Initialize Slim Select for elements with the 'select' class
    container.querySelectorAll('.select').forEach(selectElement => {
        const optionsElement = selectElement.nextElementSibling;
        const options = optionsElement?.classList.contains('select-options') ? optionsElement.innerHTML : "";
        const placeholder = selectElement.dataset.placeholder || '';
        const allowDeselect = selectElement.dataset.allowDeselect === 'true';

        new SlimSelect({
            select: selectElement,
            placeholder: placeholder,
            allowDeselect: allowDeselect,
            onChange: function (info) {
                if (info.value) {
                    Fnc.handleSelectCollapse(selectElement);
                }
            }
        });
    });

    /**
     * Updates the file input element to reflect the current state of the FilePond instance.
     * @param createdPond
     * @param inputElement
     */
    function updateInputFileElement(createdPond, inputElement) {
        const dt = new DataTransfer();
        createdPond.getFiles().forEach(f => dt.items.add(f.file));
        inputElement.files = dt.files;
    }

    // Initialize Filepond for elements with the 'filepond' class
    document.querySelectorAll('.filepond').forEach(element => {
        const parent = element.parentElement;
        const options = parent.querySelector('#file-options').innerHTML;
        const parsedOptions = options ? JSON.parse(options) : {};
        const mergedOptions = Object.assign({
            allowFileEncode: true,
            imageValidateSizeMinWidth: 224,
            imageValidateSizeMinHeight: 224
        }, parsedOptions, FilepondLocale.getFilePondTrans(APP_LANG_LOCALE));

        const createdPond = Filepond.create(element, mergedOptions);

        createdPond.on('init', () => {
            const inputElement = parent.querySelector('input[type="file"]');
            inputElement.setAttribute('data-modified-name', 'files');

            createdPond.on('addfile', (error, filePondFile) => {
                if (error) return false;
                updateInputFileElement(createdPond, inputElement);
                return true;
            });

            createdPond.on('removefile', () => {
                updateInputFileElement(createdPond, inputElement);
            });
        });
    });

    initializeDatepicker();
    initializeSimplebar();

    document.dispatchEvent(new Event('librariesInitialized'));
}

/**
 * Initialize Air Datepicker for elements with the 'datepicker' class.
 */
function initializeDatepicker() {
    document.querySelectorAll('.datepicker').forEach(datepicker => {
        const datepickerOptions = {
            range: true,
            toggleSelected: false,
            minDate: new Date(),
            dateFormat: 'yyyy-MM-dd',
            multipleDatesSeparator: ' - ',
            autoClose: true,
            locale: APP_LANG_LOCALE === 'lt' ? airDatePickerLocaleLT : airDatePickerLocaleEN
        };

        new AirDatepicker(datepicker, datepickerOptions);

        datepicker.value = '';
    });
}

/**
 * Initialize SimpleBar for elements with the 'scrollbar-element' class.
 */
export function initializeSimplebar() {
    document.querySelectorAll('.scrollbar-element').forEach(el => {
        new SimpleBar(el, {
            ...getElementDataOptions(el.dataset, 'simplebar'),
            scrollbarMinSize: 50
        });
    });
}

/**
 * Extracts configuration options from data attributes and merges them with default settings.
 *
 * @param dataset The dataset object containing data attributes.
 * @param keyword The prefix to filter relevant data attributes.
 * @returns {Object} The extracted and normalized options.
 */
function getElementDataOptions(dataset, keyword) {
    const options = {};

    for (const name in dataset) {
        if (name.startsWith(keyword)) {
            const replacedKey = name.replace(keyword, '');
            const newKey = replacedKey.charAt(0).toLowerCase() + replacedKey.slice(1);
            options[newKey] = dataset[name] === 'false' ? false : dataset[name];
        }
    }

    return options;
}
